import Phaser from "phaser";
import type { CharacterGridSpec } from "@/character/grid";
import { SceneKeys } from "@/game/data/scene-keys";
import { registerCharacterSceneAnims } from "@/game/register-character-scene-anims";
import { isDataUrl, loadHtmlImageFromDataUrl } from "@/game/load-data-texture";

export class BootScene extends Phaser.Scene {
  private bootAborted = false;

  constructor() {
    super(SceneKeys.Boot);
  }

  preload(): void {
    const idleUrl = this.registry.get("playerIdleTextureUrl") as string | undefined;
    const sheetUrl = this.registry.get("playerSpritesheetUrl") as string | undefined;
    const shadowIdleUrl = this.registry.get("shadowIdleTextureUrl") as string | undefined;
    const shadowSheetUrl = this.registry.get("shadowSpritesheetUrl") as string | undefined;
    const defShadowIdleUrl = this.registry.get("defenderShadowIdleTextureUrl") as string | undefined;
    const defShadowSheetUrl = this.registry.get("defenderShadowSpritesheetUrl") as
      | string
      | undefined;
    const petIdleUrl = this.registry.get("petIdleTextureUrl") as string | undefined;
    const petSheetUrl = this.registry.get("petSpritesheetUrl") as string | undefined;

    if (idleUrl && !isDataUrl(idleUrl)) {
      this.load.image("player-idle", idleUrl);
    }
    if (sheetUrl && !isDataUrl(sheetUrl)) {
      const grid = this.registry.get("playerSpritesheetGrid") as CharacterGridSpec | undefined;
      if (grid) {
        this.load.spritesheet("player-spritesheet", sheetUrl, {
          frameWidth: grid.frameWidth,
          frameHeight: grid.frameHeight,
        });
      } else {
        this.load.image("player-spritesheet-flat", sheetUrl);
      }
    }
    if (shadowIdleUrl && !isDataUrl(shadowIdleUrl)) {
      this.load.image("shadow-idle", shadowIdleUrl);
    }
    if (shadowSheetUrl && !isDataUrl(shadowSheetUrl)) {
      const grid = this.registry.get("shadowSpritesheetGrid") as CharacterGridSpec | undefined;
      if (grid) {
        this.load.spritesheet("shadow-spritesheet", shadowSheetUrl, {
          frameWidth: grid.frameWidth,
          frameHeight: grid.frameHeight,
        });
      }
    }
    if (defShadowIdleUrl && !isDataUrl(defShadowIdleUrl)) {
      this.load.image("def-shadow-idle", defShadowIdleUrl);
    }
    if (defShadowSheetUrl && !isDataUrl(defShadowSheetUrl)) {
      const grid = this.registry.get("defenderShadowSpritesheetGrid") as
        | CharacterGridSpec
        | undefined;
      if (grid) {
        this.load.spritesheet("def-shadow-spritesheet", defShadowSheetUrl, {
          frameWidth: grid.frameWidth,
          frameHeight: grid.frameHeight,
        });
      }
    }
    if (petIdleUrl && !isDataUrl(petIdleUrl)) {
      this.load.image("pet-idle", petIdleUrl);
    }
    if (petSheetUrl && !isDataUrl(petSheetUrl)) {
      const grid = this.registry.get("petSpritesheetGrid") as CharacterGridSpec | undefined;
      if (grid) {
        this.load.spritesheet("pet-spritesheet", petSheetUrl, {
          frameWidth: grid.frameWidth,
          frameHeight: grid.frameHeight,
        });
      }
    }
  }

  create(): void {
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.bootAborted = true;
    });
    void this.loadDataUrlTextures().finally(() => {
      if (this.bootAborted) return;
      this.finishBoot();
    });
  }

  private async loadDataUrlTextures(): Promise<void> {
    const idleUrl = this.registry.get("playerIdleTextureUrl") as string | undefined;
    const sheetUrl = this.registry.get("playerSpritesheetUrl") as string | undefined;
    const grid = this.registry.get("playerSpritesheetGrid") as CharacterGridSpec | undefined;

    if (idleUrl && isDataUrl(idleUrl) && !this.textures.exists("player-idle")) {
      const img = await loadHtmlImageFromDataUrl(idleUrl);
      this.textures.addImage("player-idle", img);
    }

    if (sheetUrl && isDataUrl(sheetUrl) && grid && !this.textures.exists("player-spritesheet")) {
      const img = await loadHtmlImageFromDataUrl(sheetUrl);
      this.textures.addSpriteSheet("player-spritesheet", img, {
        frameWidth: grid.frameWidth,
        frameHeight: grid.frameHeight,
      });
    } else if (sheetUrl && isDataUrl(sheetUrl) && !grid && !this.textures.exists("player-spritesheet-flat")) {
      const img = await loadHtmlImageFromDataUrl(sheetUrl);
      this.textures.addImage("player-spritesheet-flat", img);
    }

    const shadowIdleUrl = this.registry.get("shadowIdleTextureUrl") as string | undefined;
    const shadowSheetUrl = this.registry.get("shadowSpritesheetUrl") as string | undefined;
    const shadowGrid = this.registry.get("shadowSpritesheetGrid") as CharacterGridSpec | undefined;

    if (shadowIdleUrl && isDataUrl(shadowIdleUrl) && !this.textures.exists("shadow-idle")) {
      const img = await loadHtmlImageFromDataUrl(shadowIdleUrl);
      this.textures.addImage("shadow-idle", img);
    }
    if (
      shadowSheetUrl &&
      isDataUrl(shadowSheetUrl) &&
      shadowGrid &&
      !this.textures.exists("shadow-spritesheet")
    ) {
      const img = await loadHtmlImageFromDataUrl(shadowSheetUrl);
      this.textures.addSpriteSheet("shadow-spritesheet", img, {
        frameWidth: shadowGrid.frameWidth,
        frameHeight: shadowGrid.frameHeight,
      });
    }

    const defShadowIdleUrl = this.registry.get("defenderShadowIdleTextureUrl") as
      | string
      | undefined;
    const defShadowSheetUrl = this.registry.get("defenderShadowSpritesheetUrl") as
      | string
      | undefined;
    const defShadowGrid = this.registry.get("defenderShadowSpritesheetGrid") as
      | CharacterGridSpec
      | undefined;

    if (defShadowIdleUrl && isDataUrl(defShadowIdleUrl) && !this.textures.exists("def-shadow-idle")) {
      const img = await loadHtmlImageFromDataUrl(defShadowIdleUrl);
      this.textures.addImage("def-shadow-idle", img);
    }
    if (
      defShadowSheetUrl &&
      isDataUrl(defShadowSheetUrl) &&
      defShadowGrid &&
      !this.textures.exists("def-shadow-spritesheet")
    ) {
      const img = await loadHtmlImageFromDataUrl(defShadowSheetUrl);
      this.textures.addSpriteSheet("def-shadow-spritesheet", img, {
        frameWidth: defShadowGrid.frameWidth,
        frameHeight: defShadowGrid.frameHeight,
      });
    }

    const petIdleUrl = this.registry.get("petIdleTextureUrl") as string | undefined;
    const petSheetUrl = this.registry.get("petSpritesheetUrl") as string | undefined;
    const petGrid = this.registry.get("petSpritesheetGrid") as CharacterGridSpec | undefined;

    if (petIdleUrl && isDataUrl(petIdleUrl) && !this.textures.exists("pet-idle")) {
      const img = await loadHtmlImageFromDataUrl(petIdleUrl);
      this.textures.addImage("pet-idle", img);
    }
    if (
      petSheetUrl &&
      isDataUrl(petSheetUrl) &&
      petGrid &&
      !this.textures.exists("pet-spritesheet")
    ) {
      const img = await loadHtmlImageFromDataUrl(petSheetUrl);
      this.textures.addSpriteSheet("pet-spritesheet", img, {
        frameWidth: petGrid.frameWidth,
        frameHeight: petGrid.frameHeight,
      });
    }
  }

  private finishBoot(): void {
    if (this.bootAborted || !this.sys) return;
    const grid = this.registry.get("playerSpritesheetGrid") as CharacterGridSpec | undefined;
    if (grid) {
      registerCharacterSceneAnims(this, "player-spritesheet", "player-", grid);
    }
    const shadowGrid = this.registry.get("shadowSpritesheetGrid") as CharacterGridSpec | undefined;
    if (shadowGrid) {
      registerCharacterSceneAnims(this, "shadow-spritesheet", "shadow-", shadowGrid);
    }
    const defShadowGrid = this.registry.get("defenderShadowSpritesheetGrid") as
      | CharacterGridSpec
      | undefined;
    if (defShadowGrid) {
      registerCharacterSceneAnims(this, "def-shadow-spritesheet", "def-shadow-", defShadowGrid);
    }
    const petGrid = this.registry.get("petSpritesheetGrid") as CharacterGridSpec | undefined;
    if (petGrid) {
      registerCharacterSceneAnims(this, "pet-spritesheet", "pet-", petGrid);
    }

    if (!this.textures.exists("player-idle") && !this.textures.exists("player-spritesheet")) {
      const gfx = this.make.graphics({}, false);
      gfx.fillStyle(0x6ec8ff, 1);
      gfx.fillRect(0, 0, 32, 48);
      gfx.generateTexture("player-placeholder", 32, 48);
      gfx.destroy();
    }
    this.scene.start(SceneKeys.Preload);
  }
}
